'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { SectionHeader } from '@/components/section-header'

const stages = [
  {
    id: '0-3',
    slug: 'de-0-a-3-meses',
    title: '0 a 3 meses',
    hoverTitle: '0>3',
    bg: '/images/timeline/bg-0-3.svg',
    baby: '/images/timeline/baby-0-3.png',
    heading: '¿Es un recién nacido?',
    text: `Periodo en el que el bebé es extremadamente sensible a estímulos visuales y sonoros. Materiales de alto contraste (blanco/negro), móviles simples y objetos que fomenten la atención visual ayudan a crear conexiones cerebrales iniciales y a regular la capacidad de observar y seguir objetos.`,
  },
  {
    id: '3-6',
    slug: 'de-3-a-6-meses',
    title: '3 a 6 meses',
    hoverTitle: '3>6',
    bg: '/images/timeline/bg-3-6.svg',
    baby: '/images/timeline/baby-3-6.png',
    heading: '¿Sostiene objetos con sus manos?',
    text: `El bebé empieza a reconocer rostros y a responder socialmente (sonríe, balbucea). Le interesa explorar con las manos y la boca: juguetes que permitan agarrar, texturas suaves y actividades que fomenten el alcance y la manipulación son ideales para afinar la coordinación ojo-mano y la exploración sensorial.`,
  },
  {
    id: '6-12',
    slug: 'de-6-a-12-meses',
    title: '6 a 12 meses',
    hoverTitle: '6>12',
    bg: '/images/timeline/bg-6-9.svg',
    baby: '/images/timeline/baby-6-9.png',
    heading: 'Explorador activo',
    text: `En esta etapa el bebé se sienta, gatea o intenta desplazarse; su curiosidad por objetos pequeños crece. Materiales que fomenten la resolución de problemas simples (encajar, abrir/cerrar), juegos de causa-efecto y objetos que refinen la motricidad fina son apropiados para promover independencia y coordinación.`,
  },
  {
    id: '12-18',
    slug: 'de-12-a-18-meses',
    title: '12 a 18 meses',
    hoverTitle: '12>18',
    bg: '/images/timeline/bg-9-12.svg',
    baby: '/images/timeline/baby-9-12.png',
    heading: 'Primeros pasos hacia la autonomía',
    text: `Comienza a desarrollar hábitos de independencia: manipula objetos con más control, imita acciones y quiere participar en rutinas. Materiales de vida práctica sencillos (volcar, verter, apilar) y juguetes que permitan repetición y concentración favorecen la autonomía y el desarrollo del control de la mano.`,
  },
]

export function TimelineSection() {
  const [hovered, setHovered] = useState<string | null>(null)
  const toggleStage = (id: string) =>
    setHovered(prev => (prev === id ? null : id))

  const handleLinkClick = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }

  return (
    <section className='w-full bg-[hsl(var(--background))] py-16 px-4 md:px-8'>
      <SectionHeader
        title='kuma acompaña'
        subtitle='Presentes en cada momento de la evolución de tu hijo.'
        className='mb-14'
      />

      <div className='relative max-w-7xl mx-auto overflow-visible'>
        {/* LÍNEA DESKTOP */}
        <div className='hidden md:block absolute inset-x-0 top-[280px] z-0'>
          <Image
            src='/images/timeline/linea.svg'
            alt='Línea de tiempo'
            width={1800}
            height={90}
            className='w-full h-auto'
          />
        </div>

        {/* LÍNEA MOBILE */}
        <div className='absolute left-1/2 -translate-x-1/2 top-0 z-0 md:hidden'>
          <Image
            src='/images/timeline/linea-m.svg'
            alt='Línea de tiempo móvil'
            width={30}
            height={750}
            className='w-auto h-[550px]'
          />
        </div>

        {/* MOBILE */}
        <div className='grid grid-cols-1 gap-6 relative z-10 md:hidden'>
          {stages.map((stage, index) => {
            const tooltipText = stage.heading
              ? `${stage.heading}\n${stage.text}`
              : stage.text

            const [firstLine, ...restLines] = tooltipText.split('\n')
            const bodyText = restLines.join('\n').trim()

            return (
              <div
                key={stage.id}
                className={`relative flex flex-col ${
                  index % 2 === 0 ? 'items-start' : 'items-end'
                }`}
                onClick={() => toggleStage(stage.id)}
              >
                {/* Grupo bebé + botón */}
                <div className='flex flex-col items-center'>
                  {/* Gráfico */}
                  <div className='relative h-[120px] w-[150px] flex items-center justify-center'>
                    <Image src={stage.bg} alt='' width={200} height={200} />
                    <Image
                      src={stage.baby}
                      alt={stage.title}
                      width={110}
                      height={110}
                      className='absolute z-[2] top-2'
                    />
                    <Image
                      src='/images/timeline/bullet.svg'
                      alt='Estrella'
                      width={40}
                      height={40}
                      className='absolute -top-4 left-6 z-[3]'
                    />
                    <span className='absolute bottom-2 z-[3] text-[#FF8F4F] text-[22px] font-allround font-semibold leading-[90%]'>
                      {stage.title}
                    </span>
                  </div>

                  <Link
                    href={`/categorias/${stage.slug}`}
                    onClick={handleLinkClick}
                    className='mt-3 bg-[#FFA474] text-white text-xs font-sans font-medium px-3 py-1.5 rounded-full shadow-sm hover:bg-[#e68c5c] transition-colors'
                  >
                    Ver productos
                  </Link>
                </div>

                {hovered === stage.id && (
                  <div className='absolute -top-20 w-[320px] left-1/2 -translate-x-1/2 z-[15]'>
                    <div className='rounded-2xl bg-[#9E9283]/95 backdrop-blur-lg text-white p-3 shadow-lg max-h-[220px] overflow-y-auto'>
                      <p className='font-sans text-body-sm whitespace-pre-line'>
                        <span className='font-semibold'>{firstLine}</span>
                        {bodyText ? `\n${bodyText}` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* DESKTOP */}
        <div className='hidden md:grid md:grid-cols-4 gap-12 relative z-10 overflow-visible'>
          {stages.map((stage, idx) => {
            const tooltipText = stage.heading
              ? `${stage.heading}\n${stage.text}`
              : stage.text

            const [firstLine, ...restLines] = tooltipText.split('\n')
            const bodyText = restLines.join('\n').trim()

            return (
              <div
                key={stage.id}
                className='group relative flex flex-col items-center'
                onMouseEnter={() => setHovered(stage.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className='relative h-[340px] flex flex-col items-center justify-end'>
                  <div className='relative'>
                    <Image src={stage.bg} alt='' width={260} height={260} />
                    <Image
                      src={stage.baby}
                      alt={stage.title}
                      width={190}
                      height={190}
                      className='absolute top-14 left-1/2 -translate-x-1/2'
                    />
                    <Image
                      src='/images/timeline/bullet.svg'
                      alt='Estrella'
                      width={50}
                      height={50}
                      className={`absolute ${
                        idx === 0
                          ? 'top-10 right-20'
                          : idx === 1
                            ? 'top-20 left-8'
                            : idx === 2
                              ? 'bottom-20 right-10'
                              : 'top-8 right-10'
                      }`}
                    />
                  </div>

                  <div className='mt-6 text-center text-[#FF8F4F] text-[22px] font-allround font-semibold leading-[90%]'>
                    {stage.title}
                  </div>
                </div>

                {hovered === stage.id && (
                  <div className='absolute left-1/2 -translate-x-1/2 -top-[190px] w-[260px] z-[15]'>
                    <div className='rounded-2xl bg-[#9E9283] text-white p-4 shadow-[0_10px_25px_rgba(0,0,0,0.25)]'>
                      <div className='mb-2 text-[#FFA474] font-allround font-medium text-[18px]'>
                        {stage.hoverTitle}
                      </div>

                      <p className='font-sans font-light text-body-sm whitespace-pre-line leading-relaxed mb-3'>
                        <span className='font-semibold'>{firstLine}</span>
                        {bodyText ? `\n${bodyText}` : ''}
                      </p>

                      <Link
                        href={`/categorias/${stage.slug}`}
                        onClick={handleLinkClick}
                        className='inline-block bg-[#FFA474] hover:bg-[#e68c5c] text-white text-xs font-sans font-medium px-3 py-1.5 rounded-full transition-colors'
                      >
                        Ver productos
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
